import React, { FC, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import {
  ConfigType,
  configurationQueryKeys,
  useConfig,
  usePortalConfig,
  useUpdatePortalConfig,
} from '@portals/api/partners';
import {
  useUpdateOnboardingStep,
  WizardOnTransition,
  WizardProvider,
  WizardTransitionType,
} from '@portals/framework';
import { TenantType } from '@portals/types';

import { STEPS } from './constants';
import OnboardingLayout from './OnboardingLayout';
import { OnboardingWizardContextDataType, StepId } from './types';

const OnboardingWizard: FC = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { data: config } = useConfig();

  const { mutateAsync: updatePortalConfig } = useUpdatePortalConfig();
  const { mutateAsync: updateOnboardingStep } =
    useUpdateOnboardingStep<StepId>();
  const { data: portalConfig } = usePortalConfig();

  const { design } = portalConfig || {};

  const [contextData, setContextData] =
    useState<OnboardingWizardContextDataType>({
      // Domain
      on_xyte_domain: config[TenantType.Partner].name,

      // Design
      design,
    });

  const onTransition: WizardOnTransition<
    OnboardingWizardContextDataType,
    StepId
  > = async ({ transitionType, to }) => {
    if (transitionType === 'prev') {
      return true;
    }

    try {
      await updatePortalConfig({
        updatedConfig: {
          design: contextData.design,
        },
      });

      const updatedStepId =
        transitionType === WizardTransitionType.Finish
          ? 'complete'
          : (to as StepId);

      await updateOnboardingStep({ stepId: updatedStepId });

      queryClient.setQueryData<ConfigType>(
        configurationQueryKeys.config,
        (config) => ({
          ...config,
          [TenantType.Partner]: {
            ...config[TenantType.Partner],
            onboarding_step: updatedStepId,
          },
        })
      );

      if (transitionType === WizardTransitionType.Finish) {
        history.push('/');
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <WizardProvider<OnboardingWizardContextDataType, StepId>
      initialStep={
        (config?.[TenantType.Partner]?.onboarding_step as StepId) ||
        StepId.CustomUrl
      }
      steps={STEPS}
      contextData={contextData}
      setContextData={setContextData}
      onTransition={onTransition}
    >
      <OnboardingLayout />
    </WizardProvider>
  );
};

export default OnboardingWizard;
